<template>
    <div>
        <main>
            <component :is="story?.content?.component" v-if="story" :blok="story?.content" />
        </main>
    </div>
</template>

<script setup lang="ts">
import type { IL4ApiProductFull } from '~/types/components/L4Products';
import { paths } from '~/utils/getLanguage';
import { replaceRootHeaderAndFooter } from '~/utils/sbHelper';

const { locale, locales, t } = useI18n();

const route: any = useRoute();

// If non-supported locale, throw 404
if (!route.matched[0].path.includes(locale.value)) {
    throw createError({
        statusCode: 404,
        statusMessage: 'Page Not Found',
        fatal: true,
    });
}

// Constant variable for access to Runtime Config
const config = useRuntimeConfig();

// FEEFO API
const { feefoMerchantId } = useFeefo();

const { data: feefoData }: any = await useAsyncData(
    'feefo_' + feefoMerchantId,
    async () =>
        await $fetch(
            `https://api.feefo.com/api/20/reviews/summary/product?merchant_identifier=${feefoMerchantId}&since_period=All&unanswered_feedback=include`
        )
);

if (feefoData.value) {
    const feefoSchema = `{
    "@context": "https://schema.org/",
    "@type": "Product",
    "name": "${t('reviewSchema.name')}",
    "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": ${feefoData.value?.rating?.rating},
        "bestRating": ${feefoData.value?.rating?.max},
        "reviewCount": ${feefoData.value?.meta?.count}
    }
}`;

    useHead({
        script: [{ type: 'application/ld+json', innerHTML: feefoSchema }],
    });
}

// Need to resolve shared components to allow the storyblok API
// to expose this for us to use.
const resolveRelations: string[] = [
    'L4Page.Entity',
    'L4FooterContainer.footer',
    'L4HeaderContainer.header',
    'L4QuoteFormExtended.airport',
    'L4QuoteFormHotels.airport',
    'L4QuoteFormExtended.keysellingpoints',
    'L4BlockUSP.block',
    'L4CustomerReviews.reviewsBlock',
    'L4QuoteForm.airport',
    'L4Testimonial.review_what',
    'L4ProductHeader.product',
    'L4ProductHeader.airport',
    'L4ProductGrid.products',
    'L4Map.products',
    'L4Map.airport',
    'L4ProductGrid.products',
    'L4TrustedPartners.partners',
    'L4ProductGrid.products',
    'L4ProductMap.products',
    'L4ProductMap.airport',
    'L4AirportParkingTabs.airport',
    'L4ProductPricingTable.carParks',
    'L4CarParkingOptions.products',
    'L4ProductPricingTable.carParks',
    'L4CarParkingOptions.airport',
    'L4ProductContent.product',
    'L4TopRatedCarParks.carParks',
    'L4TransferOptionsContainer.options_component',
    'L4EmailSignupNew.signup',
];

let story: any;
const routeParts = (route.path?.startsWith('/') ? route.path.slice(1) : route.path).split('/');
const routeTypeSlug = routeParts[1];

//get country code. If country code contains additional lang. E.g. ca-fr
const countryCode = routeParts[0].length > 2 ? routeParts[0].split('-')[0] : routeParts[0];

const isTransferPage = Object.values(paths.transfers)
    .filter((slug) => slug !== '')
    .some((slug) => slug === routeTypeSlug);
const isParkingPage = Object.values(paths.parking)
    .filter((slug) => slug !== '')
    .some((slug) => slug === routeTypeSlug);
const isPortParkingPage = Object.values(paths['port-parking'])
    .filter((slug) => slug !== '')
    .some((slug) => slug === routeTypeSlug);
const isTrainStationParkingPage = Object.values(paths['train-station-parking'])
    .filter((slug) => slug !== '')
    .some((slug) => slug === routeTypeSlug);

if (isTransferPage && isTransferToLocationPage()) {
    const routePath = route.path?.startsWith('/') ? route.path.slice(1) : route.path;
    const location = routePath.split('/')[2];

    const storyblokApi = useStoryblokApi();
    story = await storyblokApi
        .get(
            'cdn/stories',
            {
                version: config?.public?.STORYBLOK_VERSION === 'published' ? 'published' : 'draft',
                by_slugs: `l4/pages/*/airport-transfers/*`,
                search_term: location,
                level: 5,
                language: getLanguage(),
                resolve_relations: resolveRelations,
            } // API Options
        )
        .then(async (data) => {
            const { stories } = data?.data || {};

            if (!stories?.length) {
                return handleStoryFetchError(new Error('Page not Found'));
            }

            const matchSlugStories = stories.filter((story) => {
                if (
                    !Object.values(paths.transfers)
                        .filter((slug) => slug !== '')
                        .some((slug) => story.full_slug.includes(`/${slug}/`))
                ) {
                    return false;
                }

                const translatedSlugs = story.translated_slugs.map((slug) =>
                    slug.path.endsWith('/') ? slug.path.slice(0, -1) : slug.path
                );

                return story.slug === location || translatedSlugs.some((slug: string) => slug.endsWith(location));
            });

            const storyData =
                matchSlugStories.find((story) => {
                    const translatedSlugs = story.translated_slugs.map((slug) =>
                        slug.path.endsWith('/') ? slug.path.slice(0, -1) : slug.path
                    );
                    return translatedSlugs.some(
                        (slug: string) => slug.includes(`l4/pages/${countryCode}/`) && slug.endsWith(location)
                    );
                }) || matchSlugStories[0];

            setSeoMeta(storyData?.content?.seo, config?.public, route);
            if (storyData.full_slug.includes(`l4/pages/${countryCode}/`)) {
                return storyData;
            }

            return await replaceRootPageContent(storyData);
        })
        .catch((err) => handleStoryFetchError(err));
} else {
    const fetchStoryOptions = {
        version:
            config?.public?.STORYBLOK_VERSION === 'published'
                ? ('published' as 'published' | 'draft')
                : ('draft' as 'published' | 'draft'),
        language: getLanguage(),
        resolve_relations: resolveRelations,
    };

    // Perform request to Storyblok to retrieve content
    story = await useAsyncStoryblok(getSbLocation(), fetchStoryOptions)
        .then((data) => {
            setSeoMeta(data?.value?.content?.seo, config?.public, route);

            return data;
        })
        .catch((err) => handleStoryFetchError(err));

    // check if it's a Transfer page for country different from current locale
    // example: /uk/airport-transfers/at
    if (isTransferToCountryPage() && import.meta.server) {
        // get transfer page for current locale
        const transferRootStory: any = await useAsyncStoryblok(getTransferRootSbLocation(), fetchStoryOptions)
            .then((data) => {
                return data;
            })
            .catch((err) => handleStoryFetchError(err));

        // replace current story Header & Footer with those from root transfer page
        const rootTransferHeader = transferRootStory?.value?.content?.page?.find(
            (component) => component.component === 'L4HeaderContainer'
        );
        const rootTransferFooter = transferRootStory?.value?.content?.page?.find(
            (component) => component.component === 'L4FooterContainer'
        );

        story.value.content.page = story.value?.content?.page?.map((component: any) => {
            if (component.component === 'L4HeaderContainer') {
                return rootTransferHeader;
            } else if (component.component === 'L4FooterContainer') {
                return rootTransferFooter;
            } else {
                return component;
            }
        });
    }
}

async function replaceRootPageContent(rootPageContent) {
    return await useAsyncStoryblok(getTransferRootSbLocation(), {
        version:
            config?.public?.STORYBLOK_VERSION === 'published'
                ? ('published' as 'published' | 'draft')
                : ('draft' as 'published' | 'draft'),
        language: getLanguage(),
        resolve_relations: resolveRelations,
    })
        .then((data) => {
            const storyData = data?.value || {};

            return replaceRootHeaderAndFooter(storyData, rootPageContent);
        })
        .catch((err) => handleStoryFetchError(err));
}

function handleStoryFetchError(err: any) {
    if (route.path === '/robots.txt') return;
    console.error('path:', route.fullPath);
    console.error(err);
    throw createError({
        statusCode: 404,
        statusMessage: 'Page Not Found',
        fatal: true,
    });
}

const locationParts = route.params.location.split('-');
const locationCode = locationParts.slice(-1)[0];
const { data: parkingProducts } = await useAsyncData<IL4ApiProductFull>(
    'products',
    () =>
        $privateApi<IL4ApiProductFull>(`parking/products/public`, {
            method: 'GET',
            params: {
                lang: getLanguage(),
                location: locationCode,
            },
        }),
    { lazy: true }
);
provide('apiParkingProducts', parkingProducts);

function getStoryblokBlockedRoutes() {
    if (config?.public.STORYBLOK_ROUTE_BLOCKED) {
        const blocked = config?.public.STORYBLOK_ROUTE_BLOCKED.split(', ');
        return blocked.some((block) => route.fullPath.includes(block));
    } else {
        return false;
    }
}

function getSbLocation() {
    let routePath = route.path?.startsWith('/') ? route.path.slice(1) : route.path;
    const routeParts = routePath.split('/');

    if (isTransferPage) {
        if (routeParts.length === 3) {
            let location = routeParts[0];
            let direction = routeParts[2] || '';
            if (routeParts[2] && routeParts[2].length === 2) {
                location = routeParts[2];
                direction = '';
            }
            routePath = `${location}/airport-transfers/${direction}`;
        }
    } else if (isParkingPage || isPortParkingPage || isTrainStationParkingPage) {
        let pageTypeSlug: string;
        if (isParkingPage) {
            pageTypeSlug = 'airport-parking';
        } else if (isPortParkingPage) {
            pageTypeSlug = 'port-parking';
        } else if (isTrainStationParkingPage) {
            pageTypeSlug = 'train-station-parking';
        }
        // replace localized page type slug with slug we have in SB
        routePath = routeParts.map((part: string, index: number) => (index === 1 ? pageTypeSlug : part)).join('/');
    }

    const location = routePath.split('/')[0];
    routePath = location.length > 2 ? routePath.replace(location, location.split('-')[0]) : routePath;
    return `/${config?.public.STORYBLOK_FOLDER}/pages/${routePath.replaceAll('%20', '-')}`;
}

// checks if it's a Transfer page for country different from current locale
// example: /uk/airport-transfers/at
function isTransferToCountryPage() {
    if (isTransferPage) {
        const routePath = route.path?.startsWith('/') ? route.path.slice(1) : route.path;
        const routeParts = routePath.split('/');
        const originalCountry = routeParts[0].length > 2 ? routeParts[0].split('-')[0] : routeParts[0];
        if (routeParts[2] && routeParts[2].length === 2 && originalCountry !== routeParts[2]) {
            return true;
        }
    }

    return false;
}

// checks if it's a Transfer page for some Location
// example: /uk/airport-transfers/manchester-man
function isTransferToLocationPage() {
    if (isTransferPage) {
        const routePath = route.path?.startsWith('/') ? route.path.slice(1) : route.path;
        const routeParts = routePath.split('/');
        if (routeParts[2] && routeParts[2].length > 2) {
            return true;
        }
    }

    return false;
}

// gets Transfer page for current locale
function getTransferRootSbLocation() {
    let routePath = route.path?.startsWith('/') ? route.path.slice(1) : route.path;
    routePath = `${countryCode}/airport-transfers`;

    return `/${config?.public.STORYBLOK_FOLDER}/pages/${routePath.replaceAll('%20', '-')}`;
}

function getLanguage() {
    const routePath = route.path?.startsWith('/') ? route.path.slice(1) : route.path;
    const location = routePath.split('/')[0];

    return location.length > 2
        ? location.split('-')[1]
        : locales.value.filter((e) => e.code === locale.value)[0].storyblok;
}

// Send user to error page if no story found
if (getStoryblokBlockedRoutes() || !story.value) {
    throw createError({
        statusCode: 404,
        statusMessage: 'Page Not Found',
        fatal: true,
    });
}

if (!import.meta.server) {
    // Allow use of Storyblok Bridge (Visual Editor)
    useStoryblokBridge(story.id, (evStory: any) => (story.value = evStory), { resolveRelations });
}
</script>
